import { useMutation } from "@apollo/client";

import {
  AiNote,
  ASSIGN_AI_NOTES_TO_TICKET_MUTATION,
  DELETE_AI_NOTE_MUTATION,
  PUBLISH_AI_NOTE_MUTATION,
  TRANSLATE_AI_NOTE_SUMMARY_MUTATION,
  UNASSIGN_AI_NOTE_FROM_TICKET_MUTATION,
  UPDATE_AI_NOTE_SUMMARY_MUTATION,
  UPDATE_AI_NOTE_TITLE_MUTATION,
} from "~/api";
import { errorsHandler } from "~/transformers";

// Both work-order mutations return the work order's full note list, but the
// server owns `Ticket.aiNotes` — refetch the work order rather than trying to
// splice the array into the cached ticket by hand. `getOwnAiNote` is refetched
// alongside it so the note detail page's "Published To" list stays in step
// (a name-based refetch only reaches queries that are currently active, so
// whichever page isn't open simply skips it).
const REFETCH = ["getOwnOemTicketById", "getOwnAiNote"];

export const useAssignAiNotesToWorkOrder = () => {
  const [assignAiNotesToTicket, { loading, error }] = useMutation(
    ASSIGN_AI_NOTES_TO_TICKET_MUTATION,
  );

  return {
    isAssigning: loading,
    error,
    assignNotes: (input: { aiNoteIds: string[]; ticketId: string }) =>
      assignAiNotesToTicket({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }).catch((err) => {
        throw errorsHandler(err);
      }),
  };
};

export const useUnassignAiNoteFromWorkOrder = () => {
  const [unassignAiNoteFromTicket, { loading, error }] = useMutation(
    UNASSIGN_AI_NOTE_FROM_TICKET_MUTATION,
  );

  return {
    isUnassigning: loading,
    error,
    unassignNote: (input: { aiNoteId: string; ticketId: string }) =>
      unassignAiNoteFromTicket({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }).catch((err) => {
        throw errorsHandler(err);
      }),
  };
};

export const useDeleteAiNote = () => {
  const [deleteAiNote, { loading, error }] = useMutation(
    DELETE_AI_NOTE_MUTATION,
  );

  return {
    isDeleting: loading,
    error,
    deleteNote: async (input) =>
      new Promise((resolve, reject) => {
        return deleteAiNote({
          variables: input,
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnAiNotes",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const usePublishAiNote = () => {
  const [publishAiNote, { loading, error }] = useMutation(
    PUBLISH_AI_NOTE_MUTATION,
  );

  return {
    isPublishing: loading,
    error,
    publishNote: async (input) =>
      new Promise((resolve, reject) => {
        return publishAiNote({
          variables: { input },
          // Publishing to a work order changes `Ticket.aiNotes`; refresh it when
          // the work order page happens to be the one that's open.
          refetchQueries: ["getOwnOemTicketById"],
          update(cache, { data: { publishAiNote: ref } }) {
            cache.modify({
              id: cache.identify({ __typename: "AiNote", id: ref._id }),
              fields: {
                getOwnAiNote(existingRefs = []) {
                  const newRef = cache.writeFragment({
                    data: ref,
                    fragment: AiNote.fragments.AiNoteData,
                    fragmentName: "AiNoteData",
                  });

                  return [...existingRefs, newRef];
                },
              },
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemMachineHistory",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemActivityLogs",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useTranslateAiNoteSummary = () => {
  const [translateAiNoteSummary, { loading, error }] = useMutation(
    TRANSLATE_AI_NOTE_SUMMARY_MUTATION,
  );

  return {
    isTranslating: loading,
    error,
    translateSummary: async (input) =>
      new Promise((resolve, reject) => {
        return translateAiNoteSummary({
          variables: { input },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useUpdateAiNoteSummary = () => {
  const [updateAiNoteSummary, { loading, error }] = useMutation(
    UPDATE_AI_NOTE_SUMMARY_MUTATION,
  );

  return {
    isUpdating: loading,
    error,
    updateSummary: async (input) =>
      new Promise((resolve, reject) => {
        return updateAiNoteSummary({
          variables: { input },
          update(cache, { data: { updateAiNoteSummary: ref } }) {
            cache.modify({
              id: cache.identify({ __typename: "AiNote", id: ref._id }),
              fields: {
                getOwnAiNote(existingRefs = []) {
                  const newRef = cache.writeFragment({
                    data: ref,
                    fragment: AiNote.fragments.AiNoteData,
                    fragmentName: "AiNoteData",
                  });

                  return [...existingRefs, newRef];
                },
              },
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useUpdateAiNoteTitle = () => {
  const [updateAiNoteTitle, { loading, error }] = useMutation(
    UPDATE_AI_NOTE_TITLE_MUTATION,
  );

  return {
    isUpdating: loading,
    error,
    updateTitle: async (input) =>
      new Promise((resolve, reject) => {
        return updateAiNoteTitle({
          variables: { input },
          update(cache, { data: { updateAiNoteTitle: ref } }) {
            cache.modify({
              id: cache.identify({ __typename: "AiNote", id: ref._id }),
              fields: {
                getOwnAiNote(existingRefs = []) {
                  const newRef = cache.writeFragment({
                    data: ref,
                    fragment: AiNote.fragments.AiNoteData,
                    fragmentName: "AiNoteData",
                  });

                  return [...existingRefs, newRef];
                },
              },
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};
